import { threeDStudioStore } from '../index'

const setGenerationState = (patch) => {
  threeDStudioStore.setState((state) => ({
    generation: { ...state.generation, ...patch },
  }))
}

/**
 * Sets whether a generation is in progress
 * @param {boolean} isGenerating - True when generation is running
 * @param {number|string|undefined} generationStartedAt - Timestamp or date string when generation started
 */
export const setIsGenerating = (isGenerating, generationStartedAt) => {
  if (isGenerating && generationStartedAt) {
    const date = new Date(generationStartedAt)
    const dateInMilliseconds = date.getTime()
    setGenerationState({ isGenerating, generationStartedAt: dateInMilliseconds })

    return
  }

  setGenerationState({
    isGenerating,
    ...(isGenerating
      ? { hasFailed: false, errorDetails: {}, errorId: '' }
      : { isEnhancing: false, triggeredBy: null }),
    generationStartedAt: isGenerating ? Date.now() : null,
  })
}

/**
 * Marks whether the in-flight generation is an enhancement (remesh/retexture/
 * segment) rather than an initial generation. Drives the "Processing..." sidebar
 * state. Cleared automatically when generation ends (see setIsGenerating).
 * @param {boolean} isEnhancing - True when an enhancement run is in progress
 */
export const setIsEnhancing = (isEnhancing) => {
  setGenerationState({ isEnhancing })
}

/**
 * Records which action button started the in-flight run so only that button shows
 * the in-progress label; every other action button is disabled with an "in
 * progress" tooltip. Cleared when generation ends (see setIsGenerating).
 * @param {string|null} triggeredBy - The triggering action key (GENERATION_TRIGGER_KEY
 *   for an initial generation, or the enhancement tab id for an enhancement run)
 */
export const setTriggeredBy = (triggeredBy) => {
  setGenerationState({ triggeredBy })
}

/**
 * Sets the session id of the model whose enhancement run is in progress. Drives
 * the "Processing..." card for that session in the library. On reload the value is
 * re-derived from the pending-runs response (see usePendingGeneratorRun).
 * @param {number|string|undefined} enhancingSessionId - The enhanced model's session id
 */
export const setEnhancingSessionId = (enhancingSessionId) => {
  setGenerationState({ enhancingSessionId })
}

/**
 * Sets whether a generation has completed at least once
 * @param {boolean} hasGenerated - True after the first successful generation
 */
export const setHasGenerated = (hasGenerated) => {
  setGenerationState({ hasGenerated })
}

/**
 * Sets the current generation run id
 * @param {number|undefined} runId - The run id from the current generation
 */
export const setRunId = (runId) => {
  setGenerationState({ runId })
}

/**
 * Sets the current generation session id
 * @param {number|undefined} sessionId - The session id from the current generation
 */
export const setSessionId = (sessionId) => {
  setGenerationState({ sessionId })
}

/**
 * Sets the last generation settings
 * @param {object} lastGenerationSettings - The last generation settings
 */
export const setLastGenerationSettings = (lastGenerationSettings) => {
  setGenerationState({ lastGenerationSettings })
}

/**
 * Sets the available credits
 * @param {number} availableCredits - The available credits
 */
export const setAvailableCredits = (availableCredits) => {
  setGenerationState({ availableCredits })
}

/**
 * Sets the generation has failed
 * @param {boolean} hasFailed - True when generation has failed
 */
export const setHasFailed = (hasFailed) => {
  setGenerationState({ hasFailed })
}

/**
 * Sets the generation error id
 * @param {string} errorId - The error id from the current generation
 */
export const setErrorId = (errorId) => {
  setGenerationState({ errorId })
}

/**
 * Sets the generation error details (failureReason, failureMessage, providerErrorCode)
 * @param {object} errorDetails - The structured error payload from the backend
 */
export const setErrorDetails = (errorDetails) => {
  setGenerationState({ errorDetails: errorDetails || {} })
}
